import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
/**
 * Placeholder card for the "apps" row at the top of the workspace. Real
 * app cards (Issues, Library, History, etc.) come online as their
 * corresponding features ship.
 */
import { Box, GitBranch, Layers, Library, Settings } from "lucide-react";

export interface WorkspaceAppsPlaceholderProps {
  onOpenSettings: () => void;
}

interface AppCard {
  id: string;
  title: string;
  description: string;
  icon: JSX.Element;
  status: "ready" | "phase_2" | "phase_3";
}

const APP_CARDS: AppCard[] = [
  {
    id: "projects",
    title: "Projects",
    description: "Browse and open ECAD projects.",
    icon: <Box className="h-4 w-4" />,
    status: "ready",
  },
  {
    id: "history",
    title: "History",
    description: "Commits, diffs, and design comparison.",
    icon: <GitBranch className="h-4 w-4" />,
    status: "phase_2",
  },
  {
    id: "library",
    title: "Library",
    description: "Catalog of components, symbols, footprints.",
    icon: <Library className="h-4 w-4" />,
    status: "phase_2",
  },
  {
    id: "release-studio",
    title: "Release studio",
    description: "Stage and publish manufacturing outputs.",
    icon: <Layers className="h-4 w-4" />,
    status: "phase_2",
  },
];

export function WorkspaceAppsPlaceholder({
  onOpenSettings,
}: WorkspaceAppsPlaceholderProps): JSX.Element {
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
      {APP_CARDS.map((app) => (
        <Card key={app.id} className="opacity-90 transition-opacity hover:opacity-100">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center justify-between text-base">
              <span className="flex items-center gap-2">
                {app.icon}
                {app.title}
              </span>
              <span className="text-[10px] font-normal uppercase tracking-wider text-muted-foreground">
                {app.status === "ready"
                  ? "Ready"
                  : `Stage ${app.status === "phase_2" ? "5-7" : "3"}`}
              </span>
            </CardTitle>
            <CardDescription>{app.description}</CardDescription>
          </CardHeader>
          <CardContent>
            <button
              type="button"
              onClick={app.id === "settings" ? onOpenSettings : undefined}
              className="text-xs font-medium text-primary hover:underline"
              disabled={app.status === "ready"}
            >
              {app.status === "ready" ? "Browse below →" : "Coming soon"}
            </button>
          </CardContent>
        </Card>
      ))}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <Settings className="h-4 w-4" />
            Settings
          </CardTitle>
          <CardDescription>Workspace, library, connectors, users.</CardDescription>
        </CardHeader>
        <CardContent>
          <button
            type="button"
            onClick={onOpenSettings}
            className="text-xs font-medium text-primary hover:underline"
          >
            Open settings
          </button>
        </CardContent>
      </Card>
    </div>
  );
}
